import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useServices } from "@/hooks/useServices";
import { Star, ArrowRight, Instagram } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import Navbar from "@/components/Navbar";
import PromoBanner from "@/components/PromoBanner";
import BookingsOpenBanner from "@/components/BookingsOpenBanner";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Lookbook } from "@/components/Lookbook";
import BeholdFeed from "@/components/BeholdFeed";
import nijahImage from "@/assets/nijah.jpeg";
import gallery1 from "@/assets/gallery-1.jpg";
import gallery2 from "@/assets/gallery-2.jpg";
import gallery3 from "@/assets/gallery-3.jpg";
import gallery4 from "@/assets/gallery-4.jpg";
import gallery5 from "@/assets/gallery-5.jpg";
import gallery6 from "@/assets/gallery-6.jpg";

const galleryImages = [
  { src: gallery1, alt: "Gold & black nail art" },
  { src: gallery2, alt: "Lash extensions" },
  { src: gallery3, alt: "Luxury pedicure" },
  { src: gallery4, alt: "Skincare products" },
  { src: gallery5, alt: "Nail polish collection" },
  { src: gallery6, alt: "Beauty salon" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.6, ease: [0, 0, 0.2, 1] as const },
  }),
};

/** Smooth wave SVG divider that morphs one section into the next */
const SectionDivider = ({ from, to, variant = "wave" }: { from: "background" | "secondary"; to: "background" | "secondary"; variant?: "wave" | "blob" | "slant" }) => {
  // Two path states for animation: resting (flatter) and active (full curve)
  const pathSets = {
    wave: {
      rest: "M0,0 L0,30 Q180,40 360,35 T720,38 T1080,32 T1440,36 L1440,0 Z",
      full: "M0,0 L0,55 Q180,120 360,75 T720,85 T1080,65 T1440,80 L1440,0 Z",
    },
    blob: {
      rest: "M0,0 L0,25 C240,40 480,20 720,30 C960,40 1200,20 1440,30 L1440,0 Z",
      full: "M0,0 L0,45 C240,130 480,15 720,75 C960,135 1200,25 1440,65 L1440,0 Z",
    },
    slant: {
      rest: "M0,0 L0,30 Q720,25 1440,28 L1440,0 Z",
      full: "M0,0 L0,100 Q720,20 1440,80 L1440,0 Z",
    },
  };

  return (
    <motion.div
      initial="rest"
      whileInView="full"
      viewport={{ once: false, margin: "-30px" }}
      className="relative h-16 md:h-28 -mt-px -mb-px overflow-hidden"
      style={{ backgroundColor: `hsl(var(--${to}))` }}
    >
      <svg
        viewBox="0 0 1440 130"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        <motion.path
          style={{ fill: `hsl(var(--${from}))` }}
          variants={{
            rest: { d: pathSets[variant].rest },
            full: { d: pathSets[variant].full },
          }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        />
      </svg>
    </motion.div>
  );
};

const testimonials = [
  { name: "Jessica M.", text: "Best nails I've ever had! The attention to detail is incredible.", rating: 5 },
  { name: "Aaliyah T.", text: "My lashes are absolutely gorgeous. I get compliments every single day!", rating: 5 },
  { name: "Maria L.", text: "The dermaplaning facial left my skin SO smooth. Obsessed!", rating: 5 },
];

const Index = () => {
  const { categories: services } = useServices();
  const { settings } = useSiteSettings();
  const instagramUrl = settings.instagram_handle.startsWith("@")
    ? `https://www.instagram.com/${settings.instagram_handle.slice(1)}`
    : `https://www.instagram.com/${settings.instagram_handle}`;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        path="/"
      />
      <Navbar />
      <BookingsOpenBanner />
      <PromoBanner />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Layered gold gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-background to-background" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-primary/8 blur-[150px] pointer-events-none" />
        <div className="absolute top-20 left-1/4 w-[350px] h-[350px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
        <div className="absolute top-32 right-1/4 w-[280px] h-[280px] rounded-full bg-primary/6 blur-[100px] pointer-events-none" />
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="w-16 h-[1px] bg-primary/60 mx-auto mb-8"
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-8"
          >
            Welcome to
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="font-serif text-5xl sm:text-7xl md:text-8xl font-bold leading-[0.95] mb-8 italic"
          >
            <span className="block">Paradise <span className="gold-shimmer not-italic">Beauty</span></span>
            <span className="block text-2xl sm:text-3xl md:text-4xl not-italic font-medium mt-5 tracking-wide text-foreground">
              Luxury Nails, Lashes & Skincare — Reynoldsburg, OH
            </span>
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="w-24 h-[1px] bg-primary/40 mx-auto mb-8"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="text-muted-foreground text-base md:text-lg max-w-lg mx-auto mb-12 font-extralight tracking-wide leading-relaxed"
          >
            Luxury nails, lashes, pedicures & dermaplaning — crafted with care, designed to make you feel beautiful.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-5 justify-center"
          >
            <Button asChild size="lg" className="rounded-none px-12 py-6 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90">
              <Link to="/book">Book Now</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-none px-12 py-6 text-xs font-medium tracking-[0.3em] uppercase border-primary/40 hover:bg-primary/10 hover:border-primary">
              <Link to="/services">View Services &amp; Experiences</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Hero → Meet Your Artist divider */}
      <SectionDivider from="background" to="secondary" variant="wave" />

      {/* Meet Your Artist */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="flex flex-col md:flex-row items-center gap-16 max-w-5xl mx-auto"
          >
            <motion.div variants={fadeUp} custom={0} className="flex-shrink-0">
              <div className="w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-2 border-primary/30 gold-glow-ring">
                <img src={nijahImage} alt="Your artist" className="w-full h-full object-cover" />
              </div>
            </motion.div>
            <div className="text-center md:text-left">
              <motion.p variants={fadeUp} custom={0} className="font-serif text-4xl md:text-5xl italic font-light text-muted-foreground mb-1">
                Meet
              </motion.p>
              <motion.h2 variants={fadeUp} custom={1} className="text-3xl md:text-4xl font-light tracking-[0.4em] uppercase mb-8">
                Your Artist
              </motion.h2>
              <motion.p variants={fadeUp} custom={2} className="text-muted-foreground text-base md:text-lg font-extralight leading-[2] tracking-wide">
                Step into my beauty world! I'm grateful for the trust of my amazing clients. I am looking forward to meeting new faces and crafting stunning looks together. Let's create pure magic!
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Meet Artist → Services divider */}
      <SectionDivider from="secondary" to="background" variant="blob" />

      {/* Service Highlights */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} custom={0} className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-4">
              What We Offer
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-serif text-4xl md:text-6xl font-bold italic">
              Services &amp; Experiences
            </motion.h2>
            <motion.div variants={fadeUp} custom={2} className="w-16 h-[1px] bg-primary/40 mx-auto mt-6" />
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, i) => (
              <motion.div
                key={service.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                custom={i}
                className="h-full"
              >
                <Link
                  to={`/services?category=${service.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="group flex flex-col h-full bg-card border border-border/50 p-10 text-center hover:border-primary/50 transition-all duration-500 hover:shadow-[0_0_40px_hsl(40_80%_42%/0.08)]"
                >
                  <span className="text-5xl block mb-6">{service.icon}</span>
                  <h3 className="font-serif text-2xl font-semibold mb-3 italic">{service.title}</h3>
                  <p className="text-muted-foreground text-sm mb-6 font-light tracking-wide flex-1">{service.tagline}</p>
                  <span className="text-primary text-xs font-light tracking-[0.3em] uppercase inline-flex items-center justify-center gap-1 group-hover:gap-3 transition-all duration-500 mt-auto">
                    Explore <ArrowRight size={12} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services → About divider */}
      <SectionDivider from="background" to="secondary" variant="slant" />

      {/* About */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="max-w-2xl mx-auto text-center"
          >
            <motion.p variants={fadeUp} custom={0} className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-4">
              About Us
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-serif text-4xl md:text-6xl font-bold italic mb-6">
              Beauty Is Pure Passion
            </motion.h2>
            <motion.div variants={fadeUp} custom={1.5} className="w-16 h-[1px] bg-primary/40 mx-auto mb-10" />
            <motion.p variants={fadeUp} custom={2} className="text-muted-foreground leading-[1.9] text-base font-extralight tracking-wide">
              At Paradise Beauty, we believe every person deserves to feel confident and radiant.
              Skilled technicians combine artistry with precision to deliver flawless results every visit.
              From stunning nail designs to voluminous lashes and glowing skin treatments, we create
              a luxurious experience tailored just for you.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* About → Testimonials divider */}
      <SectionDivider from="secondary" to="background" variant="wave" />

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} custom={0} className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-4">
              Testimonials
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-serif text-4xl md:text-6xl font-bold italic">
              What Clients Say
            </motion.h2>
            <motion.div variants={fadeUp} custom={2} className="w-16 h-[1px] bg-primary/40 mx-auto mt-6" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-5xl mx-auto">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                custom={i}
                className="bg-card border border-border/50 p-10 text-center"
              >
                <div className="flex justify-center gap-1 mb-5">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={14} className="fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-muted-foreground text-sm italic mb-8 leading-[1.9] font-light">"{t.text}"</p>
                <p className="text-xs font-medium tracking-[0.3em] uppercase">{t.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials → Gallery divider */}
      <SectionDivider from="background" to="secondary" variant="blob" />

      {/* Instagram Feed */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-14"
          >
            <motion.p variants={fadeUp} custom={0} className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-4">
              The Work
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-serif text-4xl md:text-6xl font-bold italic mb-4">
              Gallery
            </motion.h2>
            <motion.div variants={fadeUp} custom={2} className="w-16 h-[1px] bg-primary/40 mx-auto" />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={fadeUp}
            custom={0}
            className="max-w-6xl mx-auto"
          >
            {settings.behold_feed_id ? (
              <BeholdFeed feedId={settings.behold_feed_id} limit={12} className="px-2 md:px-8" />
            ) : (
              <div className="flex justify-center">
                {/* Fallback: previous embed until the Instagram feed ID is saved in settings */}
                <iframe
                  src={`https://snapwidget.com/embed/${settings.snapwidget_embed_id || '1118656'}`}
                  className="snapwidget-widget border-0 bg-transparent"
                  style={{ width: '100%', height: `${settings.snapwidget_desktop_height || 600}px`, overflow: 'hidden', border: 'none' }}
                  allowTransparency
                  scrolling="no"
                  loading="lazy"
                  title="Nails"
                />
              </div>
            )}
            <div className="text-center mt-8">
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 border border-primary/40 px-10 py-4 text-xs font-medium tracking-[0.3em] uppercase hover:bg-primary/10 hover:border-primary transition-all duration-500"
              >
                <Instagram size={16} className="text-primary" />
                Follow {settings.instagram_handle}
              </a>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Lookbook */}
      {settings.lookbook_show_on_home === "true" && (
      <section className="py-20 bg-gradient-to-b from-secondary/40 via-background to-secondary/20">

        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-12"
          >
            <motion.div variants={fadeUp} custom={0} className="w-16 h-[1px] bg-primary/40 mx-auto mb-6" />
            <motion.p variants={fadeUp} custom={0} className="text-[10px] tracking-[0.4em] uppercase text-primary mb-3">
              The Lookbook
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-serif text-4xl md:text-5xl font-bold italic mb-4">
              Flip Through The Work
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-muted-foreground text-sm md:text-base font-extralight tracking-wide max-w-md mx-auto">
              A curated book of recent sets, lashes &amp; details — turn the page.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <Lookbook />
          </motion.div>
        </div>
      </section>
      )}


      {/* Gallery → CTA divider */}
      <SectionDivider from="secondary" to="background" variant="slant" />

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-background to-primary/10">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div variants={fadeUp} custom={0} className="w-16 h-[1px] bg-primary/40 mx-auto mb-8" />
            <motion.h2 variants={fadeUp} custom={0} className="font-serif text-4xl md:text-6xl font-bold italic mb-6">
              Ready to Glow?
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-muted-foreground text-base mb-12 max-w-md mx-auto font-extralight tracking-wide leading-relaxed">
              Book your appointment today and experience the Paradise difference.
            </motion.p>
            <motion.div variants={fadeUp} custom={2}>
              <Button asChild size="lg" className="rounded-none px-14 py-6 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90">
                <Link to="/book">Book Your Appointment</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;